import { ComponentViewerError, FeaturesEvent, isComponentViewerItemReadonlyItem, } from '@standardnotes/services';
import { c, msgid } from 'ttag';
import { ComponentViewerEvent, PrefKey, MutationType, CreateDecryptedItemFromPayload, isDecryptedItem, isNotEncryptedItem, isNote, CreateComponentRetrievedContextPayload, createComponentCreatedContextPayload, DecryptedPayload, ComponentDataDomain, PayloadEmitSource, PayloadTimestampDefaults, Environment, UIFeature, } from '@standardnotes/models';
import { environmentToString, platformToString } from '@Lib/Application/Platforms';
import { AllowedBatchContentTypes, ReadwriteActions, } from './Types';
import { AppName, jtString, ComponentAction, ComponentArea, } from '@standardnotes/features';
import { isString, extendArray, Copy, removeFromArray, nonSecureRandomIdentifier, UuidGenerator, Uuids, sureSearchArray, isNotUndefined, uniqueArray, } from '@standardnotes/utils';
import { ContentType } from '@standardnotes/domain-core';
export class ComponentViewer {
    constructor(componentOrFeature, services, options, config) {
        this.componentOrFeature = componentOrFeature;
        this.services = services;
        this.options = options;
        this.config = config;
        this.identifier = nonSecureRandomIdentifier();
        this.actionObservers = [];
        this.eventObservers = [];
        this.dealloced = false;
        this.readonly = false;
        this.lockReadonly = false;
        if (isComponentViewerItemReadonlyItem(options.item)) {
            this.setReadonly(true);
            this.lockReadonly = true;
        }
        this.removeItemObserver = this.services.items.addObserver(ContentType.TYPES.Any, ({ changed, inserted, removed, source, sourceKey }) => {
            if (this.dealloced) {
                return;
            }
            const items = [...changed, ...inserted, ...removed];
            this.handleChangesInItems(items, source, sourceKey);
        });
        if (options.actionObserver) {
            this.actionObservers.push(options.actionObserver);
        }
        this.removeFeaturesObserver = services.features.addEventObserver((event) => {
            if (this.dealloced) {
                return;
            }
            if (event === FeaturesEvent.FeaturesAvailabilityChanged) {
                this.postActiveThemes();
                this.notifyEventObservers(ComponentViewerEvent.FeatureStatusUpdated);
            }
        });
        this.services.logger.info('Constructor', this);
    }
    getComponentOrFeatureItem() {
        return this.componentOrFeature;
    }
    get url() {
        return this.options.url;
    }
    get isDesktop() {
        return this.config.environment === Environment.Desktop;
    }
    get isMobile() {
        return this.config.environment === Environment.Mobile;
    }
    destroy() {
        this.services.logger.info('Destroying', this);
        this.deinit();
    }
    deinit() {
        this.dealloced = true;
        this.componentOrFeature = undefined;
        this.services = undefined;
        this.config = undefined;
        this.options = undefined;
        this.eventObservers.length = 0;
        this.actionObservers.length = 0;
        this.removeFeaturesObserver();
        this.removeFeaturesObserver = undefined;
        this.removeItemObserver();
        this.removeItemObserver = undefined;
    }
    addEventObserver(observer) {
        this.eventObservers.push(observer);
        const thislessChangeObservers = this.eventObservers;
        return () => {
            removeFromArray(thislessChangeObservers, observer);
        };
    }
    notifyEventObservers(event) {
        for (const observer of this.eventObservers) {
            observer(event);
        }
    }
    addActionObserver(observer) {
        this.actionObservers.push(observer);
        const thislessChangeObservers = this.actionObservers;
        return () => {
            removeFromArray(thislessChangeObservers, observer);
        };
    }
    setReadonly(readonly) {
        if (this.lockReadonly) {
            throw Error('Attempting to set readonly on lockedReadonly component viewer');
        }
        this.readonly = readonly;
    }
    get componentUniqueIdentifier() {
        return this.componentOrFeature.uniqueIdentifier;
    }
    getFeatureStatus() {
        return this.services.features.getFeatureStatus(this.componentUniqueIdentifier, {
            inContextOfItem: this.getContextItem(),
        });
    }
    getContextItem() {
        if (isComponentViewerItemReadonlyItem(this.options.item)) {
            return this.options.item.readonlyItem;
        }
        const matchingItem = this.services.items.findItem(this.options.item.uuid);
        return matchingItem;
    }
    isOfflineRestricted() {
        return this.componentOrFeature.isComponent && this.componentOrFeature.asComponent.offlineOnly && !this.isDesktop;
    }
    hasUrlError() {
        if (!this.componentOrFeature.isComponent) {
            return false;
        }
        return this.isDesktop
            ? !this.componentOrFeature.asComponent.local_url && !this.componentOrFeature.asComponent.hasValidHostedUrl
            : !this.componentOrFeature.asComponent.hasValidHostedUrl;
    }
    shouldRender() {
        return this.getError() == undefined;
    }
    getError() {
        if (this.isOfflineRestricted()) {
            return ComponentViewerError.OfflineRestricted;
        }
        if (this.hasUrlError()) {
            return ComponentViewerError.MissingUrl;
        }
        return undefined;
    }
    updateOurComponentRefFromChangedItems(items) {
        if (!this.componentOrFeature.isComponent) {
            return;
        }
        const updatedComponent = items.find((item) => item.uuid === this.componentUniqueIdentifier.value);
        if (!updatedComponent) {
            return;
        }
        const item = new UIFeature(updatedComponent);
        this.componentOrFeature = item;
    }
    handleChangesInItems(items, source, sourceKey) {
        const nonencryptedItems = items.filter(isNotEncryptedItem);
        const nondeletedItems = nonencryptedItems.filter(isDecryptedItem);
        this.updateOurComponentRefFromChangedItems(nondeletedItems);
        const areWeOriginator = sourceKey && sourceKey === this.componentUniqueIdentifier.value;
        if (areWeOriginator) {
            return;
        }
        if (this.streamItems) {
            const relevantItems = nonencryptedItems.filter((item) => {
                var _a;
                return (_a = this.streamItems) === null || _a === void 0 ? void 0 : _a.includes(item.content_type);
            });
            if (relevantItems.length > 0) {
                this.sendManyItemsThroughBridge(relevantItems);
            }
        }
        if (this.streamContextItemOriginalMessage) {
            const optionsItem = this.options.item;
            if (isComponentViewerItemReadonlyItem(optionsItem)) {
                return;
            }
            const matchingItem = nondeletedItems.find((item) => item.uuid === optionsItem.uuid);
            if (matchingItem) {
                this.sendContextItemThroughBridge(matchingItem, source);
            }
        }
    }
    sendManyItemsThroughBridge(items) {
        var _a;
        const requiredPermissions = [
            {
                name: ComponentAction.StreamItems,
                content_types: (_a = this.streamItems) === null || _a === void 0 ? void 0 : _a.sort(),
            },
        ];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredPermissions, () => {
            this.sendItemsInReply(items, this.streamItemsOriginalMessage);
        });
    }
    sendContextItemThroughBridge(item, source) {
        const requiredContextPermissions = [
            {
                name: ComponentAction.StreamContextItem,
            },
        ];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredContextPermissions, () => {
            this.services.logger.info('Send context item in reply', 'component:', this.componentOrFeature, 'item: ', item, 'originalMessage: ', this.streamContextItemOriginalMessage);
            const response = {
                item: this.jsonForItem(item, source),
            };
            this.replyToMessage(this.streamContextItemOriginalMessage, response);
        });
    }
    sendItemsInReply(items, message, source) {
        this.services.logger.info('Send items in reply', this.componentOrFeature, items, message);
        const responseData = {};
        const mapped = items.map((item) => {
            return this.jsonForItem(item, source);
        });
        responseData.items = mapped;
        this.replyToMessage(message, responseData);
    }
    jsonForItem(item, source) {
        const isMetadatUpdate = source === PayloadEmitSource.RemoteSaved ||
            source === PayloadEmitSource.OfflineSyncSaved ||
            source === PayloadEmitSource.PreSyncSave;
        const params = {
            uuid: item.uuid,
            content_type: item.content_type,
            created_at: item.created_at,
            updated_at: item.serverUpdatedAt,
            isMetadataUpdate: isMetadatUpdate,
        };
        if (isDecryptedItem(item)) {
            params.content = this.contentForItem(item);
            params.clientData = this.getClientData(item);
        }
        else {
            params.deleted = true;
        }
        return this.responseItemsByRemovingPrivateProperties([params])[0];
    }
    getClientData(item) {
        const globalComponentData = item.getDomainData(ComponentDataDomain) || {};
        const thisComponentData = globalComponentData[this.componentUniqueIdentifier.value] || {};
        return thisComponentData;
    }
    contentForItem(item) {
        if (isNote(item)) {
            const content = item.content;
            const spellcheck = item.spellcheck != undefined
                ? item.spellcheck
                : this.services.preferences.getValue(PrefKey.EditorSpellcheck, true);
            return {
                ...content,
                spellcheck,
            };
        }
        return item.content;
    }
    replyToMessage(originalMessage, replyData) {
        const reply = {
            action: ComponentAction.Reply,
            original: originalMessage,
            data: replyData,
        };
        this.sendMessage(reply);
    }
    /**
     * @param essential If the message is non-essential, no alert will be shown
     *  if we can no longer find the window.
     */
    sendMessage(message, essential = true) {
        if (!this.window && message.action === ComponentAction.Reply) {
            this.services.logger.info('Component has been deallocated in between message send and reply', this.componentOrFeature, message);
            return;
        }
        this.services.logger.info('Send message to component', this.componentOrFeature, 'message: ', message);
        if (!this.window) {
            if (essential) {
                const componentDisplayName = this.componentOrFeature.displayName;
                void this.services.alerts.alert(jtString(c('B4.Notes.EditingUI.Error')
                    .jt `${AppName} is trying to communicate with ${componentDisplayName}, but an error is occurring. Please restart this extension and try again.`));
            }
            return;
        }
        /** Because iframes do not allow-same-origin, their origin is `null`, and so we can't target their explicit origin */
        const nullOrigin = '*';
        /* Mobile messaging requires json */
        this.window.postMessage(this.isMobile ? JSON.stringify(message) : message, nullOrigin);
    }
    responseItemsByRemovingPrivateProperties(responseItems, removeUrls = false) {
        /* Don't allow component to overwrite these properties. */
        let privateContentProperties = ['autoupdateDisabled', 'permissions', 'active'];
        if (removeUrls) {
            privateContentProperties = privateContentProperties.concat(['hosted_url', 'local_url']);
        }
        return responseItems.map((responseItem) => {
            const privateProperties = privateContentProperties.slice();
            /** Server extensions are allowed to modify url property */
            if (removeUrls) {
                privateProperties.push('url');
            }
            if (!responseItem.content || isString(responseItem.content)) {
                return responseItem;
            }
            let content = {};
            for (const [key, value] of Object.entries(responseItem.content)) {
                if (!privateProperties.includes(key)) {
                    content = {
                        ...content,
                        [key]: value,
                    };
                }
            }
            return {
                ...responseItem,
                content: content,
            };
        });
    }
    /** Called by client when the iframe is ready */
    setWindow(window) {
        var _a;
        if (this.window) {
            throw Error('Attempting to override component viewer window. Create a new component viewer instead.');
        }
        this.services.logger.info('setWindow', 'component: ', this.componentOrFeature, 'window: ', window);
        this.window = window;
        this.sessionKey = UuidGenerator.GenerateUuid();
        const componentData = (_a = this.config.componentManagerFunctions.getComponentPreferences(this.componentOrFeature)) !== null && _a !== void 0 ? _a : {};
        this.sendMessage({
            action: ComponentAction.ComponentRegistered,
            sessionKey: this.sessionKey,
            componentData: componentData,
            data: {
                uuid: this.componentUniqueIdentifier.value,
                environment: environmentToString(this.config.environment),
                platform: platformToString(this.config.platform),
                activeThemeUrls: this.config.componentManagerFunctions.urlsForActiveThemes(),
            },
        });
        this.services.logger.info('setWindow got new sessionKey', this.sessionKey);
        this.postActiveThemes();
    }
    postActiveThemes() {
        const urls = this.config.componentManagerFunctions.urlsForActiveThemes();
        const data = {
            themes: urls,
        };
        const message = {
            action: ComponentAction.ActivateThemes,
            data: data,
        };
        this.sendMessage(message, false);
    }
    handleMessage(message) {
        this.services.logger.info('Handle message', message, this);
        if (!this.componentOrFeature) {
            this.services.logger.info('Component not defined for message, returning', message);
            void this.services.alerts.alert(jtString(c('B4.Notes.EditingUI.Error')
                .jt `A component is trying to communicate with ${AppName}, but there is an error establishing a bridge. Please restart the app and try again.`));
            return;
        }
        if (this.readonly && ReadwriteActions.includes(message.action)) {
            const componentDisplayName = this.componentOrFeature.displayName;
            void this.services.alerts.alert(c('B4.Notes.EditingUI.Error')
                .t `${componentDisplayName} is trying to save, but it is in a locked state and cannot accept changes.`);
            return;
        }
        const messageHandlers = {
            [ComponentAction.StreamItems]: this.handleStreamItemsMessage.bind(this),
            [ComponentAction.StreamContextItem]: this.handleStreamContextItemMessage.bind(this),
            [ComponentAction.SetComponentData]: this.handleSetComponentPreferencesMessage.bind(this),
            [ComponentAction.DeleteItems]: this.handleDeleteItemsMessage.bind(this),
            [ComponentAction.CreateItems]: this.handleCreateItemsMessage.bind(this),
            [ComponentAction.CreateItem]: this.handleCreateItemsMessage.bind(this),
            [ComponentAction.SaveItems]: this.handleSaveItemsMessage.bind(this),
            [ComponentAction.SetSize]: this.handleSetSizeEvent.bind(this),
        };
        const handler = messageHandlers[message.action];
        handler === null || handler === void 0 ? void 0 : handler(message);
        for (const observer of this.actionObservers) {
            observer(message.action, message.data);
        }
    }
    handleStreamItemsMessage(message) {
        const data = message.data;
        const types = data.content_types.filter((type) => AllowedBatchContentTypes.includes(type)).sort();
        const requiredPermissions = [
            {
                name: ComponentAction.StreamItems,
                content_types: types,
            },
        ];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredPermissions, () => {
            if (!this.streamItems) {
                this.streamItems = types;
                this.streamItemsOriginalMessage = message;
            }
            /* Push immediately now */
            const items = [];
            for (const contentType of types) {
                extendArray(items, this.services.items.getItems(contentType));
            }
            this.sendItemsInReply(items, message);
        });
    }
    handleStreamContextItemMessage(message) {
        const requiredPermissions = [
            {
                name: ComponentAction.StreamContextItem,
            },
        ];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredPermissions, () => {
            if (!this.streamContextItemOriginalMessage) {
                this.streamContextItemOriginalMessage = message;
            }
            const matchingItem = isComponentViewerItemReadonlyItem(this.options.item)
                ? this.options.item.readonlyItem
                : this.services.items.findItem(this.options.item.uuid);
            if (matchingItem) {
                this.sendContextItemThroughBridge(matchingItem);
            }
        });
    }
    /**
     * Save items is capable of saving existing items, and also creating new ones
     * if they don't exist.
     */
    handleSaveItemsMessage(message) {
        let responsePayloads = message.data.items;
        const requiredPermissions = [];
        /* Pending as in needed to be accounted for in permissions. */
        const pendingResponseItems = responsePayloads.slice();
        if (isComponentViewerItemReadonlyItem(this.options.item)) {
            return;
        }
        for (const responseItem of responsePayloads.slice()) {
            if (responseItem.uuid === this.options.item.uuid) {
                requiredPermissions.push({
                    name: ComponentAction.StreamContextItem,
                });
                removeFromArray(pendingResponseItems, responseItem);
                /* We break because there can only be one context item */
                break;
            }
        }
        /* Check to see if additional privileges are required */
        if (pendingResponseItems.length > 0) {
            const requiredContentTypes = uniqueArray(pendingResponseItems.map((item) => {
                return item.content_type;
            })).sort();
            requiredPermissions.push({
                name: ComponentAction.StreamItems,
                content_types: requiredContentTypes,
            });
        }
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredPermissions, async () => {
            responsePayloads = this.responseItemsByRemovingPrivateProperties(responsePayloads, true);
            /* Filter locked items */
            const uuids = Uuids(responsePayloads);
            const items = this.services.items.findItemsIncludingBlanks(uuids);
            let lockedCount = 0;
            let lockedNoteCount = 0;
            for (const item of items) {
                if (!item) {
                    continue;
                }
                if (item.locked) {
                    responsePayloads = responsePayloads.filter((responseItem) => {
                        return responseItem.uuid !== item.uuid;
                    });
                    lockedCount++;
                    if (item.content_type === ContentType.TYPES.Note) {
                        lockedNoteCount++;
                    }
                }
            }
            if (lockedNoteCount === 1) {
                void this.services.alerts.alert(c('B4.Notes.EditingUI.Error').t `The note you are attempting to save has editing disabled`, c('B4.Notes.EditingUI.Title').t `Note has Editing Disabled`);
                return;
            }
            else if (lockedCount > 0) {
                const editingDisabledMessage = lockedCount === 1
                    ? c('B4.Notes.EditingUI.Error').t `The item you are attempting to save has editing disabled.`
                    : lockedNoteCount === lockedCount
                        ? c('B4.Notes.EditingUI.Error').t `The notes you are attempting to save have editing disabled.`
                        : c('B4.Notes.EditingUI.Error').t `The items you are attempting to save have editing disabled.`;
                void this.services.alerts.alert(editingDisabledMessage, c('B4.Notes.EditingUI.Title').t `Items have Editing Disabled`);
                return;
            }
            const contextualPayloads = responsePayloads.map((responseItem) => {
                return CreateComponentRetrievedContextPayload(responseItem);
            });
            for (const contextualPayload of contextualPayloads) {
                const item = this.services.items.findItem(contextualPayload.uuid);
                if (!item) {
                    const payload = new DecryptedPayload({
                        ...PayloadTimestampDefaults(),
                        ...contextualPayload,
                    });
                    const template = CreateDecryptedItemFromPayload(payload);
                    await this.services.mutator.insertItem(template);
                }
                else {
                    if (contextualPayload.content_type !== item.content_type) {
                        throw Error('Extension is trying to modify content type of item.');
                    }
                }
            }
            await this.services.mutator.changeItems(items.filter(isNotUndefined), (mutator) => {
                const contextualPayload = sureSearchArray(contextualPayloads, {
                    uuid: mutator.getUuid(),
                });
                mutator.setCustomContent(contextualPayload.content);
                const responseItem = sureSearchArray(responsePayloads, {
                    uuid: mutator.getUuid(),
                });
                if (responseItem.clientData) {
                    const allComponentData = Copy(mutator.getItem().getDomainData(ComponentDataDomain) || {});
                    allComponentData[this.componentUniqueIdentifier.value] = responseItem.clientData;
                    mutator.setDomainData(allComponentData, ComponentDataDomain);
                }
            }, MutationType.UpdateUserTimestamps, PayloadEmitSource.ComponentRetrieved, this.componentUniqueIdentifier.value);
            this.services.sync
                .sync({
                onPresyncSave: () => {
                    this.replyToMessage(message, {});
                },
            })
                .catch(() => {
                this.replyToMessage(message, {
                    error: 'save-error',
                });
            });
        });
    }
    handleCreateItemsMessage(message) {
        let responseItems = (message.data.item ? [message.data.item] : message.data.items);
        const uniqueContentTypes = uniqueArray(responseItems.map((item) => {
            return item.content_type;
        }));
        const requiredPermissions = [
            {
                name: ComponentAction.StreamItems,
                content_types: uniqueContentTypes,
            },
        ];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredPermissions, async () => {
            responseItems = this.responseItemsByRemovingPrivateProperties(responseItems);
            const processedItems = [];
            for (const responseItem of responseItems) {
                if (!responseItem.uuid) {
                    responseItem.uuid = UuidGenerator.GenerateUuid();
                }
                const contextualPayload = createComponentCreatedContextPayload(responseItem);
                const payload = new DecryptedPayload({
                    ...PayloadTimestampDefaults(),
                    ...contextualPayload,
                });
                const template = CreateDecryptedItemFromPayload(payload);
                const item = await this.services.mutator.insertItem(template);
                await this.services.mutator.changeItem(item, (mutator) => {
                    if (responseItem.clientData) {
                        const allComponentClientData = Copy(item.getDomainData(ComponentDataDomain) || {});
                        allComponentClientData[this.componentUniqueIdentifier.value] = responseItem.clientData;
                        mutator.setDomainData(allComponentClientData, ComponentDataDomain);
                    }
                }, MutationType.UpdateUserTimestamps, PayloadEmitSource.ComponentCreated, this.componentUniqueIdentifier.value);
                processedItems.push(item);
            }
            void this.services.sync.sync();
            const reply = message.action === ComponentAction.CreateItem
                ? { item: this.jsonForItem(processedItems[0]) }
                : {
                    items: processedItems.map((item) => {
                        return this.jsonForItem(item);
                    }),
                };
            this.replyToMessage(message, reply);
        });
    }
    handleDeleteItemsMessage(message) {
        const data = message.data;
        const items = data.items.filter((item) => AllowedBatchContentTypes.includes(item.content_type));
        const requiredContentTypes = uniqueArray(items.map((item) => item.content_type)).sort();
        const requiredPermissions = [
            {
                name: ComponentAction.StreamItems,
                content_types: requiredContentTypes,
            },
        ];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, requiredPermissions, async () => {
            const itemsData = items;
            const itemCount = itemsData.length;
            let reply = null;
            const didConfirm = await this.services.alerts.confirm(c('B3.Notes.NoteActions.Confirmation').ngettext(msgid `Are you sure you want to delete ${itemCount} item?`, `Are you sure you want to delete ${itemCount} items?`, itemCount));
            if (didConfirm) {
                /* Filter for any components and deactivate before deleting */
                for (const itemData of itemsData) {
                    const item = this.services.items.findItem(itemData.uuid);
                    if (!item) {
                        void this.services.alerts.alert(c('B3.Notes.NoteActions.Error').t `The item you are trying to delete cannot be found.`);
                        continue;
                    }
                    await this.services.mutator.setItemToBeDeleted(item, PayloadEmitSource.ComponentRetrieved);
                }
                void this.services.sync.sync();
                reply = { deleted: true };
            }
            else {
                /* Rejected by user */
                reply = { deleted: false };
            }
            this.replyToMessage(message, reply);
        });
    }
    handleSetComponentPreferencesMessage(message) {
        const noPermissionsRequired = [];
        this.config.componentManagerFunctions.runWithPermissionsUseCase.execute(this.componentUniqueIdentifier.value, noPermissionsRequired, async () => {
            const newPreferences = message.data.componentData;
            if (!newPreferences) {
                return;
            }
            await this.config.componentManagerFunctions.setComponentPreferences(this.componentOrFeature, newPreferences);
        });
    }
    handleSetSizeEvent(message) {
        var _a;
        if (this.componentOrFeature.area !== ComponentArea.EditorStack) {
            return;
        }
        const parent = (_a = this.getIframe()) === null || _a === void 0 ? void 0 : _a.parentElement;
        if (!parent) {
            return;
        }
        const data = message.data;
        const widthString = isString(data.width) ? data.width : `${data.width}px`;
        const heightString = isString(data.height) ? data.height : `${data.height}px`;
        if (parent) {
            parent.setAttribute('style', `width:${widthString}; height:${heightString};`);
        }
    }
    getIframe() {
        return Array.from(document.getElementsByTagName('iframe')).find((iframe) => iframe.dataset.componentViewerId === this.identifier);
    }
}
